import { getColors } from "@/pages/mill/gantt/components/GanttChart";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Chip, Paper, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { formatDateTime, formatNumber } from "./utils";

type Props = {
	sectionId: number | null;
	sectionName: string | null;
	rollingStart: Date | null;
	endTime: Date | null;
	schedFinishedTons: number | null;
	actualFinishedTons?: number | null;
	schedFinishedYield: number | null;
	budgetTPH: number | null;
	isCurrent: boolean;
	heatName?: string | null;
	gradeName?: string | null;
};

const Row = ({
	label,
	description,
	value,
	valueColor,
	valueNode,
}: {
	label: string;
	description: string;
	value?: string | null;
	valueColor?: string;
	valueNode?: ReactNode;
}) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "space-between",
			alignItems: "baseline",
			py: 0.25,
		}}
	>
		<Tooltip arrow placement="top" title={description}>
			<Typography
				sx={{
					fontSize: "0.72rem",
					color: "#777",
				}}
			>
				{label}
			</Typography>
		</Tooltip>
		{valueNode ?? (
			<Typography
				sx={{
					fontSize: value ? "0.8rem" : "0.72rem",
					fontWeight: 600,
					color: value ? (valueColor ?? "#1a1a1a") : "#777",
				}}
			>
				{value ?? "-"}
			</Typography>
		)}
	</Box>
);

export const SectionPanel = ({
	sectionId,
	sectionName,
	rollingStart,
	endTime,
	schedFinishedTons,
	actualFinishedTons,
	schedFinishedYield,
	budgetTPH,
	isCurrent,
	heatName,
	gradeName,
}: Props) => {
	const hasSection = sectionId != null && sectionName != null;
	const colors = hasSection ? getColors(sectionId) : null;

	return (
		<Paper
			elevation={0}
			sx={{
				p: 1.25,
				border: `1px ${isCurrent ? "solid" : "dashed"} #e0e0e0`,
				borderRadius: 1,
				backgroundColor: "#fff",
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box
						sx={{
							width: 7,
							height: 7,
							borderRadius: "50%",
							backgroundColor: (theme) =>
								isCurrent
									? theme.palette.primary.main
									: theme.palette.success.main,
						}}
					/>
					<Typography
						sx={{
							fontSize: "0.68rem",
							fontWeight: 700,
							color: (theme) =>
								isCurrent
									? theme.palette.primary.main
									: theme.palette.success.main,
						}}
					>
						{isCurrent ? "CURRENT" : "NEXT"}
					</Typography>
				</Box>
				{hasSection && colors ? (
					<Chip
						label={sectionName}
						size="small"
						sx={{
							backgroundColor: colors.fill,
							color: colors.text,
							border: `1px solid ${colors.border}`,
							fontWeight: 700,
							fontSize: "0.75rem",
							height: 22,
						}}
					/>
				) : (
					<Typography
						variant="caption"
						sx={{ color: "#aaa", fontStyle: "italic" }}
					>
						No event
					</Typography>
				)}
			</Box>

			{hasSection && (
				<Box>
					{isCurrent && (
						<>
							<Row
								label="Heat"
								description="Heat currently being rolled in this event."
								value={heatName ?? null}
							/>
							<Row
								label="Grade"
								description="Grade of the heat currently being rolled."
								value={gradeName ?? null}
							/>
						</>
					)}
					<Row
						label={isCurrent ? "Rolling Start" : "Scheduled Start"}
						description={
							isCurrent
								? "Timestamp of the first billet rolled in this event."
								: "Planned start time for this event."
						}
						value={dxtToLocalServerTime(rollingStart, "MM/dd HH:mm") || null}
					/>
					<Row
						label="Scheduled End"
						description={
							isCurrent
								? "Scheduled end time for the current event."
								: "Scheduled end time for this event."
						}
						value={formatDateTime(endTime)}
					/>
					<Row
						label="Finished Tons"
						description={
							isCurrent
								? "Actual Finished Tons produced so far in this event."
								: "Scheduled Finished Tons"
						}
						value={formatNumber(
							isCurrent ? actualFinishedTons : schedFinishedTons,
							0,
						)}
					/>
					<Row
						label="Finished Yield"
						description="Scheduled Finished Yield"
						value={
							schedFinishedYield != null
								? `${formatNumber(schedFinishedYield, 1)}%`
								: "-"
						}
					/>
					<Row
						label="Budget TPH"
						description="Budget Tons Per Hour for this event."
						value={formatNumber(budgetTPH, 1)}
					/>
				</Box>
			)}
		</Paper>
	);
};
